import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../../components/blocks/AnimatedSection';
import PartnerCard from '../../components/blocks/PartnerCard';
import { getEventImagePath } from '../../config/eventPaths';
import { getPartnersByKeys, MONGOLIAN_CRITICAL_ZONE_PARTNER_KEYS } from '../../config/partners';

const MongolianCriticalZone = () => {
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [revealedPosters, setRevealedPosters] = useState(new Set());
    
    // Review images from the Review folder (standard path: events/titan/mongolian-critical-zone/Review/)
    const reviewImages = Array.from({ length: 10 }, (_, i) =>
        getEventImagePath('titan', 'mongolian-critical-zone', `Review/${i}.jpeg`)
    );

    const nextImage = () => {
        setCurrentImageIndex((prev) => (prev + 1) % reviewImages.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prev) => (prev - 1 + reviewImages.length) % reviewImages.length);
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="mb-12">
                        <img
                            src={getEventImagePath('titan', 'mongolian-critical-zone', 'Experiential Learning in the Winter Steppes.jpg')}
                            alt="Mongolian Critical Zone"
                            className="w-full h-full md:h-96 object-cover rounded-lg shadow-lg"
                            style={{ maxWidth: '100%', height: '100%' }}
                        />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mt-8 mb-4">Mongolian Critical Zone</h1>
                    <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-4">
                        Experiential Learning in the Winter Steppes
                    </p>
                    <p className="inline-block px-4 py-2 rounded-full bg-gray-200 text-gray-700 font-semibold text-sm mb-6">
                        Application closed
                    </p>
                </div>

                {/* Overview Section */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Overview</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                            Conduct in-situ transdisciplinary research in Ulaanbaatar, Mongolia. Engage with sustainable urban living practices in Ger areas with <b>GerHub</b>. Empower youth in marginalised communities with <b>Nuudelch Zuslan</b>.
                        </p>
                        <div className="grid md:grid-cols-2 gap-6 mt-6">
                            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Date</h3>
                                <p className="text-gray-700">12th-18th, January, 2026</p>
                            </div>
                            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Location</h3>
                                <p className="text-gray-700">Ulaanbaatar, Mongolia</p>
                            </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-6 mt-6">
                            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Expected Deliverables</h3>
                                <ul className="list-disc list-inside text-gray-700 space-y-1">
                                    <li>Individual Experiential Research Poster</li>
                                    <li>Group Documentary Video</li>
                                </ul>
                            </div>
                            <div className="p-4 border rounded-lg hover:shadow-md transition-shadow">
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Reimbursement</h3>
                                <p className="text-gray-700">
                                    <span className="font-bold text-black">HK$800 (Self-funded airfare)</span> <br /> Accommodation &amp; local transportation are provided.
                                </p>
                            </div>
                        </div>

                        {/* Our Partners */}
                        <div className="mt-8">
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Partners</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 items-stretch justify-items-center">
                                {getPartnersByKeys(MONGOLIAN_CRITICAL_ZONE_PARTNER_KEYS).map((partner) => (
                                    <PartnerCard
                                        key={partner.id}
                                        partner={partner}
                                        className="p-6 rounded-xl w-full md:max-w-xs min-h-[200px]"
                                        logoClassName="h-20 w-auto object-contain mb-3"
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Program Highlights */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Programme Highlights</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <div className="space-y-6">
                            <div className="border-l-4 border-blue-500 pl-6 rounded-lg transition-all duration-200 hover:bg-blue-50 hover:border-blue-600 hover:shadow-md cursor-pointer">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-1 pt-4 pb-6">
                                        <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">Transdisciplinary Research</h3>
                                        <p className="text-gray-700 leading-relaxed text-justify">
                                            Conduct in-situ transdisciplinary research in Ulaanbaatar, Mongolia.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="border-l-4 border-green-500 pl-6 rounded-lg transition-all duration-200 hover:bg-green-50 hover:border-green-600 hover:shadow-md cursor-pointer">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-1 pt-4 pb-6">
                                        <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">Community Engagement</h3>
                                        <p className="text-gray-700 leading-relaxed text-justify">
                                            Engage with sustainable urban living practices in Ger areas with <b>GerHub</b>.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-6 rounded-lg transition-all duration-200 hover:bg-purple-50 hover:border-purple-600 hover:shadow-md cursor-pointer">
                                <div className="flex items-start space-x-4">
                                    <div className="flex-1 pt-4 pb-6">
                                        <h3 className="text-xl font-semibold text-gray-900 mt-2 mb-2">Cross-Cultural Learning</h3>
                                        <p className="text-gray-700 leading-relaxed text-justify">
                                            Empower youth in marginalised communities with <b>Nuudelch Zuslan</b>.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Trip Review & Gallery */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Trip Review</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        {/* Summary Video */}
                        <div className="mb-10">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Video Highlights</h3>
                            <div className="aspect-video w-full max-w-4xl mx-auto rounded-lg overflow-hidden shadow-md">
                                <iframe
                                    src="https://drive.google.com/file/d/1Dt9TClisZAz_wnwRB2uKtANA529VQDHn/preview"
                                    className="w-full h-full"
                                    allow="autoplay"
                                    allowFullScreen
                                    title="Mongolian Critical Zone Summary Video"
                                />
                            </div>
                        </div>

                        {/* Instagram-style card */}
                        <div className="max-w-3xl mx-auto">
                            <h3 className="text-xl font-semibold text-gray-900 mb-4">Instagram Post</h3>
                            <div className="flex items-center mb-4">
                                <a
                                    href="https://www.instagram.com/titanprojectshk/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center hover:opacity-80 transition-opacity"
                                >
                                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-gray-200">
                                        <img
                                            src="/asset/icons/TITAN_Logo_Original.jpg"
                                            alt="TITAN Logo"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="ml-3">
                                        <p className="text-sm font-semibold text-gray-900">titanprojectshk</p>
                                        <p className="text-xs text-gray-500">@titanprojectshk · Instagram</p>
                                    </div>
                                </a>
                            </div>
                            
                            {/* Scrollable Image Gallery */}
                            <div className="relative mb-4 border border-gray-200 rounded-xl overflow-hidden bg-white">
                                <div className="relative flex items-center justify-center">
                                    <img
                                        src={reviewImages[currentImageIndex]}
                                        alt={`Mongolian Critical Zone trip — ${currentImageIndex + 1}`}
                                        className="w-full h-auto object-contain"
                                    />
                                    
                                    {/* Navigation Arrows */}
                                    {reviewImages.length > 1 && (
                                        <>
                                            <button
                                                onClick={prevImage}
                                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 z-10"
                                                aria-label="Previous image"
                                            >
                                                <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={nextImage}
                                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 z-10"
                                                aria-label="Next image"
                                            >
                                                <svg className="w-6 h-6 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                </svg>
                                            </button>
                                        </>
                                    )}
                                    
                                    {/* Image Indicator Dots */}
                                    {reviewImages.length > 1 && (
                                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2 z-10 bg-black/30 backdrop-blur-sm px-3 py-1.5 rounded-full">
                                            {reviewImages.map((_, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentImageIndex(index)}
                                                    className={`h-2 rounded-full transition-all duration-200 ${
                                                        index === currentImageIndex ? 'bg-white w-6' : 'bg-white/50 w-2'
                                                    }`}
                                                    aria-label={`Go to image ${index + 1}`}
                                                />
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            
                            <div className="border border-gray-200 rounded-xl overflow-hidden">
                                <div className="p-4 space-y-3 text-sm leading-relaxed text-gray-800">
                                    <p>12/1-18/1 Mongolian Critical Zone: Experiential Learning in the Winter Steppes</p>
                                    <p>
                                        We are proud to share that our experiential trip to Ulaanbaatar, Mongolia with 13 HKU students,
                                        co-facilitated by @yayaycchen, has been successfully completed!
                                    </p>
                                    <p>
                                        Braving temperatures near -40°C, we explored life in Ulaanbaatar’s Ger districts with @gerhubmongolia
                                        by visiting the Ger Innovation Hub and the Ger Plug-in (co-created by @hkuarchitecture) while learning
                                        about the carbon monoxide and heat loss problems prevalent in the areas.
                                    </p>
                                    <p>
                                        We also visited Magic Mongolia Child Development Center @idshidiinoron_official, the National University
                                        of Mongolia, and the bustling Narantuul “Black Market”.
                                    </p>
                                    <p>
                                        Finally, we journeyed to the countryside to experience traditional nomadic life, horseback riding across
                                        frozen steppes and learning from local herding families.
                                    </p>
                                    <p>
                                        From extreme urban cold to the windswept countryside, our students gained invaluable insights into
                                        Mongolia’s unique social, environmental, and cultural landscape.
                                    </p>
                                    <p>
                                        We extend our deepest gratitude to our facilitators, organisers from @nuudelch.zuslan, and the local
                                        communities in Mongolia who made this transformative journey possible. Your hospitality and knowledge
                                        sharing created an unforgettable learning experience for our students.
                                    </p>
                                    <p>
                                        This trip is funded by the Transdisciplinary Experiential Learning Fund by @commoncorehku.
                                    </p>
                                    <p>
                                        Stay tuned for our upcoming documentary capturing this incredible journey! 🎥❄️🐴
                                    </p>
                                </div>
                                <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-between">
                                    <span className="text-xs text-gray-500">View full post on Instagram</span>
                                    <a
                                        href="https://www.instagram.com/p/DUM80-sk9VB/?utm_source=ig_web_copy_link&igsh=NTc4MTIwNjQ2YQ=="
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs font-semibold text-blue-600 hover:text-blue-700"
                                    >
                                        Open in Instagram
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </AnimatedSection>

                {/* Research Output */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Research Output</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify mb-6">
                            Explore the experiential research posters produced by participating students.
                        </p>
                        <div className="mb-10">
                            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Featured Posters</h3>
                            <div className="grid gap-4 md:grid-cols-2">
                                <div className="flex flex-col justify-between rounded-lg border-2 border-blue-300 p-4 shadow-md hover:shadow-lg transition-shadow bg-white">
                                    <h4 className="text-base font-semibold text-gray-900 mb-3 text-center leading-snug">
                                        The Undying Legacy of Chinggis Khaan: How Temüjin's Influence Lived on in Modern Day Mongolia
                                    </h4>
                                    <div className="mb-3 rounded-md overflow-hidden border border-gray-200 bg-gray-50 flex justify-center">
                                        <img
                                            src={getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Fan Chun Kiu.png')}
                                            alt="Fan Chun Kiu poster"
                                            className="max-w-full w-auto h-auto object-contain"
                                        />
                                    </div>
                                    <div className="mt-2 flex justify-center">
                                        <a
                                            href={getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Fan Chun Kiu.pdf')}
                                            download
                                            className="inline-flex items-center justify-center rounded-full border border-blue-400 px-5 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 transition"
                                        >
                                            Download Featured Poster
                                        </a>
                                    </div>
                                </div>
                                <div className="flex flex-col justify-between rounded-lg border-2 border-orange-300 p-4 shadow-md hover:shadow-lg transition-shadow bg-white">
                                    <h4 className="text-base font-semibold text-gray-900 mb-3 text-center leading-snug">
                                        Mongolia's Transnational Sex Trafficking Crisis: Assessing the Causes and the Potential for Strengthened China-Mongolia Anti-Trafficking Cooperation
                                    </h4>
                                    <div className="mb-3 rounded-md overflow-hidden border border-gray-200 bg-gray-50 flex justify-center">
                                        <img
                                            src={getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Leung Yee Kiu.png')}
                                            alt="Leung Yee Kiu poster"
                                            className="max-w-full w-auto h-auto object-contain"
                                        />
                                    </div>
                                    <div className="mt-2 flex justify-center">
                                        <a
                                            href={getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Leung Yee Kiu.pdf')}
                                            download
                                            className="inline-flex items-center justify-center rounded-full border border-orange-400 px-5 py-2 text-sm font-semibold text-orange-700 hover:bg-orange-50 transition"
                                        >
                                            Download Featured Poster
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mb-4">
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">All Posters</h3>
                            <p className="text-sm text-gray-600">
                                Browse the full collection of student posters from the Mongolian Critical Zone programme.
                            </p>
                        </div>
                        <div className="flex flex-wrap md:-mx-4 items-start">
                            {[
                                {
                                    name: 'Banipal Gurrajpreet',
                                    title: 'Mongolia Like Us; Merely Mortal',
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Banipal Gurrajpreet.pdf'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Banipal Gurrajpreet.png'),
                                },
                                {
                                    name: 'Chiu Wai Fu',
                                    title: "How Mongolia's 1990 Democratic Transition and Subsequent Public Policies Have Influenced Urban Challenges in Ulaanbaatar's Ger Districts",
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Chiu Wai Fu.pdf'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Chiu Wai Fu/1.png'),
                                },
                                {
                                    name: 'Chiu Yat Him',
                                    title: 'Critical Analysis on Urbanisation in Ulaanbaatar with Heating and Mongolian Nomadic Lifestyle in Ger',
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Chiu Yat Him.png'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Chiu Yat Him.png'),
                                },
                                {
                                    name: 'Han Yishan',
                                    title: "Learning in the Smoke and Frost: An Ethnographic Study of Education, Resilience, and Identity in Ulaanbaatar's Ger District",
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Han Yishan.png'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Han Yishan.png'),
                                },
                                {
                                    name: 'Hui Chun Hei Alex',
                                    title: "Ger Settlements in Ulaanbaatar: A Brief History and Current Issues",
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Hui Chun Hei Alex.png'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Hui Chun Hei Alex.png'),
                                },
                                {
                                    name: 'Ku Yan Ming',
                                    title: "Air Quality and Health Impacts in Ulaanbaatar's Ger Districts During Winter",
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Ku Yan Ming.pdf'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Ku Yan Ming.png'),
                                },
                                {
                                    name: 'Lee Ching',
                                    title: "The Forgotten Outskirts of the World's Coldest Capital",
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Lee Ching.pdf'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Lee Ching.png'),
                                },
                                {
                                    name: 'Szeto Wing Kiu',
                                    title: "Orientation Report: An Analysis on Non-Government Organizations' Role and Dilemmas in Education for Marginalized Students in Mongolia",
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Szeto Wing Kiu.pdf'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Szeto Wing Kiu/1.png'),
                                },
                                {
                                    name: 'Theng Min Xuan',
                                    title: "Air Pollution Issue in Ulaanbaatar: Observations and Reality",
                                    href: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Theng Min Xuan.pdf'),
                                    type: 'image',
                                    thumbnailSrc: getEventImagePath('titan', 'mongolian-critical-zone', 'Output/Theng Min Xuan.png'),
                                },
                            ].map((output) => {
                                const isRevealed = revealedPosters.has(output.name);
                                const title = output.title || output.name;
                                return (
                                    <div key={output.name} className="w-full md:w-1/2 md:px-4 mb-6">
                                        <div className="flex flex-col justify-between rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow-md transition-shadow bg-white">
                                            <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center leading-snug">
                                                {title}
                                            </h3>
                                            {output.type === 'image' && (
                                                <div className="mb-3">
                                                    {isRevealed ? (
                                                        <div className="rounded-md overflow-hidden border border-gray-200 bg-gray-50">
                                                            <img
                                                                src={output.thumbnailSrc}
                                                                alt={`${output.name} poster`}
                                                                className="w-full h-auto object-contain"
                                                            />
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="w-full rounded-md border border-dashed border-gray-300 bg-gray-50 py-3 px-4 text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
                                                            onClick={() =>
                                                                setRevealedPosters((prev) => {
                                                                    const next = new Set(prev);
                                                                    next.add(output.name);
                                                                    return next;
                                                                })
                                                            }
                                                        >
                                                            Show Poster
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {output.type !== 'image' && (
                                            <div className="mb-3 flex h-48 items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50">
                                                <div className="text-center">
                                                    <div className="mx-auto mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-700">
                                                        <span className="text-xs font-bold">PDF</span>
                                                    </div>
                                                    <p className="text-xs font-medium text-gray-600">Poster available as PDF</p>
                                                </div>
                                            </div>
                                            )}
                                            <div className="mt-2">
                                                <p className="text-sm font-semibold text-gray-700 text-center mb-2">{output.name}</p>
                                                <div className="flex justify-center">
                                                    <a
                                                        href={output.href}
                                                        download
                                                        className="inline-flex items-center justify-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:border-orange-400 hover:text-orange-600 transition"
                                                    >
                                                        Download Poster
                                                    </a>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </AnimatedSection>

                {/* Connection to TITAN */}
                <AnimatedSection
                    className="mb-16"
                    heading={<h2 className="text-3xl font-semibold text-gray-900 mb-8">Organised by TITAN</h2>}
                >
                    <div className="bg-white rounded-lg shadow-md p-8">
                        <p className="text-lg text-gray-700 leading-relaxed text-justify">
                            The Transdisciplinary Initiative for Talents and Innovators (TITAN) is a student-powered initiative promoting transdisciplinary thinking, research, and experiential learning. We are a Student Learning Partner with HKU Common Core, and are recognized by United Nations Academic Impact as part of the Millenium Fellowship Class of 2025 (Hong Kong campus).
                        </p>
                        <div className="mt-6">
                            <Link
                                to="/titan"
                                className="inline-block px-6 py-3 rounded-full border border-gray-300 text-gray-700 font-semibold hover:border-orange-400 hover:text-orange-600 transition"
                            >
                                Learn More About TITAN →
                            </Link>
                        </div>
                    </div>
                </AnimatedSection>
            </div>
        </div>
    );
};

export default MongolianCriticalZone;

